<svelte:window on:resize={onResize} />
<div class="plan-page__image-container">
    <img
        bind:this={planImageElement}
        class="plan-page__image"
        {style}
        src={imageUrl || placeholderImageUrl}
        alt={imageDescription}
    />

    {#if !imageUrl && !placeholderImageUrl}<div class="plan-page__image-icon"></div>{/if}

    {#if canEdit}
        <div class="plan-page__edit-image">
            {#if imageUrl}
                <Button
                    type="secondary"
                    text={config.isImageUploaderInPlansEnabled ? i18n.editImage : i18n.removeImage}
                    on:click={config.isImageUploaderInPlansEnabled ? editImage : removeImage}
                    disabled={removePending}
                    loading={removePending}
                />
            {:else}
                <Button
                    type="secondary"
                    text={i18n.addImage}
                    on:click={config.isImageUploaderInPlansEnabled ? addUploadImage: addImage}
                    disabled={imageUploading}
                    loading={imageUploading}
                />
            {/if}

            {#if config.isImageUploaderInPlansEnabled}
                <ImageUploadModal
                    bind:showModal={showModal}
                    planId={planId}
                    imageUploaderFileRepoSettings={config.imageUploaderFileRepoSettings}
                    imageUploaderFileValidationSettings={config.imageUploaderFileValidationSettings}
                    imageUrl={imageUrl}
                    showImageDescription={true}
                    imageDescriptionMaxLength={config.imageDescriptionMaxLength}
                    imageDescription={imageDescription}
                    on:saveCancelAction={onSaveCancelAction}
                />
            {:else}
                <input
                    bind:this={fileInput}
                    hidden
                    type="file"
                    accept="image/*"
                    on:change={filePicked}
                />
            {/if}
        </div>
    {/if}
</div>

{#if errorMessage}
    <!-- eslint-disable-next-line svelte/no-at-html-tags -->
    <p class="plan-page__image-error">{@html errorMessage}</p>
{/if}

<script>
    import { onMount } from "svelte";
    import Button from "@itslearning/prometheus/assets/inputs/Button/v1/Button.svelte";
    import ImageUploadModal from "@its-ui/planner-common/components/ImageUploader/ImageUploadModal.svelte";
    import config from "@its-ui/planner-common/config";
    import i18n from "@its-ui/planner-common/i18n";
    import { deleteImage, setImage } from "@its-ui/planner-common/controllers/updatePlanController";

    export let planId;
    export let imageUrl;
    export let topic;
    export let canEdit;
    export let placeholderImageUrl;
    export let imageDescription;

    let errorMessage = undefined;
    let fileInput;
    let imageUploading = false;
    let removePending = false;
    let width = 420;
    let planImageElement = undefined;
    let height = 294;
    let showModal = false;

    onMount(() => onResize());

    $: style = [
        topic && !imageUrl ? `background-color: ${topic.color}` : null,
        imageUrl ? `height: ${height}px` : null
    ]
        .filter(s => s)
        .join(";");

    function onResize() {
        width = planImageElement.getBoundingClientRect().width;

        // 0.7 maintains the aspect ratio plan images should always be diplayed at
        height = width * 0.7;

    }

    function addImage() {
        fileInput.click();
    }

    async function filePicked() {
        errorMessage = undefined;

        if (!fileInput.files.length) {
            return;
        }

        const imageFile = fileInput.files[0];

        imageUploading = true;
        fileInput.value = null;

        try {
            const data = await setImage(planId, imageFile);

            if (!data.success && data.errorMessage) {
                errorMessage = data.errorMessage;
            } else {
                const contentMatch = data.content.match(/<img.*src="([^"]+)".*>/);

                if (contentMatch.length > 1) {
                    imageUrl = contentMatch[1];
                }
            }

            imageUploading = false;
        } catch (e) {
            if (e.errorMessage) {
                errorMessage = e.errorMessage;
            }

            imageUploading = false;
        }
    }

    async function removeImage() {
        errorMessage = undefined;
        removePending = true;

        try {
            const plan = await deleteImage(planId);

            imageUrl = plan.imageUrl;
            removePending = false;
        } catch (e) {
            if (e.errorMessage) {
                errorMessage = e.errorMessage;
            }

            removePending = false;
        }
    }

    function addUploadImage() {
        showModal = true;
    }

    async function editImage() {
        errorMessage = undefined;
        showModal = true;
    }

    function onSaveCancelAction({ detail: { planImageUpdates } }) {
        errorMessage = planImageUpdates.imageErrorMessage;

        if (planImageUpdates.isFileRemoved) {
            errorMessage = undefined;
            imageUrl = undefined;
            imageDescription = undefined;
        } else if (planImageUpdates.isSave && planImageUpdates.apiUploadSuccess) {
            imageUrl = planImageUpdates.imageUrl;
            imageDescription = planImageUpdates.imageDescription;
        } else {
            imageDescription = imageDescription;
        }
    }

</script>
